import { AUTH_API_PATHS } from '@/lib/auth/paths'
import { sanitizeRedirectPath } from '@/lib/auth/safeRedirect'

export type AdminLoginPromptMode = 'login' | 'none'

const DEFAULT_ADMIN_REDIRECT_PATH = '/admin'
const DEFAULT_ADMIN_LOGIN_PATH = '/admin/login'

function toRelativeUrl(
  pathname: string,
  searchParams: URLSearchParams
): string {
  const search = searchParams.toString()
  return `${pathname}${search ? `?${search}` : ''}`
}

export function buildAdminLoginPagePath(options?: {
  redirectPath?: string | null
  errorCode?: string | null
}): string {
  const redirectPath = sanitizeRedirectPath(
    options?.redirectPath,
    DEFAULT_ADMIN_REDIRECT_PATH
  )
  const url = new URL(DEFAULT_ADMIN_LOGIN_PATH, 'https://placeholder.invalid')
  url.searchParams.set('redirect', redirectPath)

  const errorCode = options?.errorCode?.trim()
  if (errorCode) {
    url.searchParams.set('error', errorCode)
  }

  return toRelativeUrl(url.pathname, url.searchParams)
}

export function buildAdminEnsureSessionPath(
  redirectPath: string | null | undefined
): string {
  const safeRedirect = sanitizeRedirectPath(
    redirectPath,
    DEFAULT_ADMIN_REDIRECT_PATH
  )
  const url = new URL(AUTH_API_PATHS.ADMIN_ENSURE_SESSION, 'https://placeholder.invalid')
  url.searchParams.set('redirect', safeRedirect)
  return toRelativeUrl(url.pathname, url.searchParams)
}

export function buildAdminLoginStartPath(options?: {
  redirectPath?: string | null
  prompt?: AdminLoginPromptMode | null
  failureRedirectPath?: string | null
}): string {
  const redirectPath = sanitizeRedirectPath(
    options?.redirectPath,
    DEFAULT_ADMIN_REDIRECT_PATH
  )
  const prompt = options?.prompt === 'none' ? 'none' : 'login'
  const url = new URL(AUTH_API_PATHS.ADMIN_LOGIN_START, 'https://placeholder.invalid')
  url.searchParams.set('redirect', redirectPath)

  if (prompt === 'none') {
    url.searchParams.set('prompt', 'none')
  }

  const failureRedirectPath = options?.failureRedirectPath
    ? sanitizeRedirectPath(options.failureRedirectPath, DEFAULT_ADMIN_LOGIN_PATH)
    : null
  if (failureRedirectPath) {
    url.searchParams.set('failureRedirect', failureRedirectPath)
  }

  return toRelativeUrl(url.pathname, url.searchParams)
}

export function startAdminSsoLogin(options?: {
  redirectPath?: string | null
  prompt?: AdminLoginPromptMode | null
  failureRedirectPath?: string | null
}): boolean {
  if (typeof window === 'undefined') {
    return false
  }

  window.location.assign(buildAdminLoginStartPath(options))
  return true
}
